import { useCallback, useEffect, useRef, useState } from 'react'
import { getLatestRun, getRun, startRun } from '../services/prospectingApi'
import type { ProspectingRun } from '../types'

const POLL_MS = 4000

const isActive = (run: ProspectingRun | null) => run !== null && (run.status === 'queued' || run.status === 'running')

/**
 * Starts a prospecting run and polls it until it finishes. Picks up a run
 * already in progress on mount, so leaving and returning to the page keeps
 * showing it. `onFinished` fires once when a polled run completes or fails.
 */
export function useProspectingRun(workspaceId: string, onFinished: (run: ProspectingRun) => void) {
  const [run, setRun] = useState<ProspectingRun | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished

  useEffect(() => {
    let cancelled = false
    getLatestRun(workspaceId)
      .then((latest) => { if (!cancelled && isActive(latest)) setRun(latest) })
      .catch(() => { /* the run banner is optional — the queue still works */ })
    return () => { cancelled = true }
  }, [workspaceId])

  const runId = run?.id
  const active = isActive(run)
  useEffect(() => {
    if (!runId || !active) return
    let cancelled = false
    const timer = window.setInterval(() => {
      getRun(workspaceId, runId)
        .then((latest) => {
          if (cancelled) return
          setRun(latest)
          if (!isActive(latest)) onFinishedRef.current(latest)
        })
        .catch(() => { /* transient — try again next tick */ })
    }, POLL_MS)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [workspaceId, runId, active])

  const start = useCallback(async () => {
    setStarting(true)
    setStartError(null)
    try {
      setRun(await startRun(workspaceId))
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Could not start prospecting')
    } finally {
      setStarting(false)
    }
  }, [workspaceId])

  return { run, active, starting, startError, start }
}
